import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, HeartPulse, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { nudgeAccentStyles } from "@/components/nudges/accent-styles";
import { pulseChatNudgeDismissKey } from "@/components/nudges/types";
import type { PulseCompletionWithFrequency } from "@/hooks/use-pulse-check-in-nudge";
import { formatPulsePeriodLabel } from "@/lib/pulse-period-label";
import { cn } from "@/lib/utils";
import { LogEvents, track } from "@/analytics/track";

type Props = {
  summary: PulseCompletionWithFrequency;
  onPrefillPrompt: (prompt: string) => void;
};

export function PulseCheckInChatNudge({ summary, onPrefillPrompt }: Props) {
  const { t } = useTranslation("intelligence");
  const period = summary.checkInPeriod ?? summary.period;
  const periodStart = period.periodStart;
  const periodEnd = period.periodEnd;
  const pulseUrl = `/coach/performance/pulse?${new URLSearchParams({
    periodStart,
    periodEnd,
  }).toString()}`;
  const dismissKey = pulseChatNudgeDismissKey(periodStart);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(dismissKey) === "true",
  );

  useEffect(() => {
    if (!dismissed) {
      track({
        event: LogEvents.PulseNudgeShown.name,
        channel: LogEvents.PulseNudgeShown.channel,
        surface: "coach",
      });
    }
  }, [dismissed]);

  if (dismissed) return null;

  const periodLabel = formatPulsePeriodLabel(
    summary.period.periodStart,
    summary.period.periodEnd,
  );
  const description =
    summary.scoredCount > 0
      ? t("pulse.checkInNudge.assessed", {
          done: summary.scoredCount,
          total: summary.totalMembers,
          period: periodLabel,
        })
      : t("pulse.checkInNudge.newPeriod", { period: periodLabel });

  const accent = nudgeAccentStyles.warning;

  const handleDismiss = () => {
    sessionStorage.setItem(dismissKey, "true");
    setDismissed(true);
    track({
      event: LogEvents.PulseNudgeDismiss.name,
      channel: LogEvents.PulseNudgeDismiss.channel,
      surface: "coach",
    });
  };

  const handleCta = () => {
    track({
      event: LogEvents.PulseNudgeCta.name,
      channel: LogEvents.PulseNudgeCta.channel,
      surface: "coach",
      action: "start_pulse",
    });
  };

  const handleCoachHelp = () => {
    track({
      event: LogEvents.PulseNudgeCta.name,
      channel: LogEvents.PulseNudgeCta.channel,
      surface: "coach",
      action: "prefill_coach",
    });
    onPrefillPrompt(t("pulse.checkInNudge.coachPrompt"));
  };

  return (
    <div
      className={cn(
        "mb-4 w-full rounded-xl border border-border border-l-4 p-4 text-left shadow-sm",
        accent.rail,
        accent.card,
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
            accent.icon,
          )}
        >
          <HeartPulse className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-semibold text-foreground">
                {t("pulse.checkInNudge.title")}
              </p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {description}
              </p>
            </div>
            <button
              type="button"
              onClick={handleDismiss}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={t("pulse.checkInNudge.closeLabel")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {summary.totalMembers > 0 ? (
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{
                  width: `${(summary.scoredCount / summary.totalMembers) * 100}%`,
                }}
              />
            </div>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button asChild size="sm" onClick={handleCta}>
              <Link to={pulseUrl}>
                {t("pulse.checkInNudge.startButton")}
                <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCoachHelp}
            >
              {t("pulse.checkInNudge.coachButton")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
